/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Divider, Form, Layout } from '../../element';
import { DropOffMap } from './drop-off-map';
import DepartmentAutocomplete from './fields/departments-autocomplete';

import { isLive } from '../../../config';
import { isAlfred, isGLS, isInPost, isHermes, isSF, isRusPost } from '../../../config/providers';

import { getAlertErrors, getBookService, showDropOffMap } from '../../../entities/providers/selectors';
import { isCourierDelivery } from '../../../entities/rates/selectors';

import { resetAlert } from '../../../entities/providers';
import { useCountry } from '../../../entities/providers/hooks';

import { FieldKey, getLabel, pickFields } from './fields';
import { useProviderForm } from './hooks';

import { useTranslation } from '../../../i18n';

export const mapFieldKey = (key: string): string => {
  if (key === 'receiver_postCodeAutoComplete') {
    return 'receiver_postCode';
  }

  return key;
};

export const AlertErrors: React.FC = () => {
  const alerts = useSelector(getAlertErrors);
  const dispatch = useDispatch();

  return (
    <>
      {alerts && (
        <div className="mt-20">
          {alerts.map((alert, idx) => (
            <Alert
              key={`${alert.key}_${idx.toString()}`}
              title={`[${alert.key}] ${alert.text}`}
              type="error"
              showIcon
              closable
              className="is-light mb-20"
              onClose={() => dispatch(resetAlert({ key: alert.key }))}
            />
          ))}
        </div>
      )}
    </>
  );
};

const Department: React.FC = () => {
  const type = 'department';

  const [t] = useTranslation('rates');

  return (
    <>
      <Layout.Row gutter={20}>
        <Layout.Col span={24}>
          <h2>{t('rates.form.departmentLabel')}</h2>

          <div>
            <Form.Item prop={`sender_${type}`} label={t(getLabel(type))}>
              <DepartmentAutocomplete type="sender" />
            </Form.Item>
          </div>
        </Layout.Col>
      </Layout.Row>
    </>
  );
};

export const ProviderAddressForm: React.FC = () => {
  const [t] = useTranslation('rates');

  const { provider } = useCountry('sender');
  const { provider: receiveProvider } = useCountry('receiver');
  const dropOffMap = useSelector(showDropOffMap);
  const service = useSelector(getBookService);
  const isCourier: boolean = useSelector(isCourierDelivery);

  /**
   * List of the hidden (but mounted) form fields
   */
  const senderHiddenList = useMemo(
    () =>
      [
        // was before
        // isInPostCourier(provider, service) && 'sendMethod'
        // now we will hide sendMethod, but it should be mounted
        // in order to set default value
        isInPost(provider) && 'sendMethod',
      ].filter<FieldKey>(Boolean as any) as FieldKey[],
    [provider, service]
  );

  const senderList = useMemo(() => {
    const keys: FieldKey[] = [
      isLive && dropOffMap && 'dropOffPoint',
      isInPost(provider) && 'sendMethod',
      'email',
      'phone',
      isGLS(provider) && 'fixedPhone',
      'firstName',
      'lastName',
      'street',
      isHermes(provider) && 'houseName',
      'buildingNumber',
      'apartments',
      isSF(provider) && 'cityText',
      !isSF(provider) && !isAlfred(provider) && 'city',
      isAlfred(provider) && 'cityDropdown',
      isAlfred(provider) && 'region',
      !isAlfred(provider) && 'postCode',
      (isHermes(provider) || isGLS(provider)) && 'dispatchDate',
    ].filter<FieldKey>(Boolean as any);

    return pickFields(...keys);
  }, [provider]);

  const receiverList = useMemo(() => {
    const isRusPostEnabled = isRusPost(receiveProvider);

    const isPlainCity = isRusPostEnabled && !isCourier;

    const keys: FieldKey[] = [
      'phone',
      'firstName',
      'lastName',
      'street',
      'buildingNumber',
      'apartments',
      !isRusPostEnabled && 'postCodeAutoComplete',
      isRusPostEnabled && 'postCode',
      isPlainCity ? 'cityText' : 'city',
      'region',
    ].filter<FieldKey>(Boolean as any);

    return pickFields(...keys);
  }, [receiveProvider, isCourier]);

  const mapProps = useProviderForm('showMap', 'sender');
  const dropOffPoint = useProviderForm('dropOffPoint', 'sender');

  return (
    <>
      <Alert type="warning" showIcon title={t('rates.form.alert')} closable className="is-light" />

      <AlertErrors />

      <div hidden={!mapProps.value}>
        <DropOffMap
          onSelect={(point) => {
            dropOffPoint.onChange(point.name);
            mapProps.onChange(false);
          }}
        />
      </div>

      <Layout.Row gutter={20}>
        <Layout.Col span={24} xs={24} sm={24} md={12} lg={12}>
          <h2>{t('rates.form.sender')}</h2>

          {senderList.map(({ type, Component }, idx) => (
            <div key={idx.toString()} hidden={senderHiddenList.includes(type)}>
              <Form.Item prop={`sender_${type}`} label={t(getLabel(type))}>
                <Component type="sender" />
              </Form.Item>
            </div>
          ))}
        </Layout.Col>

        {isAlfred(provider) && (
          <Layout.Col span={24} xs={24} className="hidden-md-and-up">
            <Department />
            <Divider type="horizontal" />
          </Layout.Col>
        )}

        <Layout.Col span={24} xs={24} sm={24} md={12} lg={12}>
          <h2>{t('rates.form.receiver')}</h2>

          {receiverList.map(({ type, Component }, idx) => (
            <Form.Item key={idx.toString()} prop={mapFieldKey(`receiver_${type}`)}>
              <Component type="receiver" />
            </Form.Item>
          ))}
        </Layout.Col>
      </Layout.Row>

      {isAlfred(provider) && (
        <div className="hidden-sm-and-down">
          <Department />
        </div>
      )}
    </>
  );
};
